/**
 * 收集当前账号的完整本地数据并打包成加密备份。
 *
 * 这里只读：IndexedDB 六张业务表 + 图库 + 本地备份流水、localStorage 白名单、
 * Mimo 持久音频库，以及服务端的只读钱包 / 余额流水 / TTS 偏好。
 * 任何非关键数据读取失败都转成 manifest 警告，不阻断本地数据导出。
 */
import { apiClient } from '../../core/api';
import type { ApiUser, LedgerEntry, WalletSnapshot } from '../../core/api';
import { getDb } from '../../core/db';
import type { ChatMessage, Moment, Preset } from '../../core/types';
import { mimoClient } from '../mimo/client';
import { preferencesSchema } from '../mimo/contracts';
import { mimoStorage } from '../mimo/storage';
import type { GalleryImage } from '../gallery/galleryRepo';
import { buildBackupFilename } from '../settings/backup';
import { encryptContainer } from './container';
import { bytesToHex } from './codec';
import { collectBackupLocalStorage } from './local-storage';
import { MediaCollector } from './media';
import { packArchive } from './archive';
import type { ArchivePayload, BackupCollections } from './archive';
import { BackupError } from './types';
import type {
  BackupAccountHistory,
  BackupApi,
  BackupCounts,
  BackupManifest,
  BackupMimoApi,
  BackupMimoFile,
  BackupTtsApi,
  CreateBackupOptions,
  EncryptedBackup,
} from './types';

const LEDGER_PAGE_SIZE = 50;
const LEDGER_MAX_PAGES = 200;

const defaultBackupApi: BackupApi = {
  currentUser: () => apiClient.currentUser(),
  wallet: () => apiClient.wallet(),
  ledgerPage: (offset = 0, limit = LEDGER_PAGE_SIZE) => apiClient.ledgerPage(offset, limit),
};

const defaultTtsApi: BackupTtsApi = {
  account: () => mimoClient.account(),
  savePreferences: (preferences) => mimoClient.savePreferences(preferences),
};

export { defaultTtsApi };

function secureRandomBytes(length: number): Uint8Array {
  if (typeof crypto === 'undefined' || typeof crypto.getRandomValues !== 'function') {
    throw new BackupError('FORMAT', '当前环境不支持安全随机数');
  }
  return crypto.getRandomValues(new Uint8Array(length));
}

async function resolveAccount(options: CreateBackupOptions, api: BackupApi): Promise<{ id: string; email: string }> {
  if (options.account !== undefined) {
    if (options.account.id.trim() === '') throw new BackupError('LOGIN_REQUIRED', '请先登录再导出备份');
    return { id: options.account.id, email: options.account.email };
  }
  let user: ApiUser;
  try {
    user = await api.currentUser();
  } catch {
    throw new BackupError('LOGIN_REQUIRED', '请先登录再导出备份');
  }
  if (user.status === 'disabled') throw new BackupError('LOGIN_REQUIRED', '当前账户不可用');
  if (typeof user.id !== 'string' || user.id.trim() === '') {
    throw new BackupError('LOGIN_REQUIRED', '无法识别当前账户');
  }
  return { id: user.id, email: typeof user.email === 'string' ? user.email : '' };
}

async function collectLedger(
  api: BackupApi,
  accountId: string,
  capturedAt: number,
): Promise<BackupAccountHistory> {
  let wallet: WalletSnapshot | null = null;
  try {
    wallet = await api.wallet();
  } catch {
    // Wallet availability is not required for a local backup.
  }

  const entries: LedgerEntry[] = [];
  const seen = new Set<string>();
  let included = true;
  let warning: string | undefined;
  let offset = 0;
  try {
    for (let page = 0; page < LEDGER_MAX_PAGES; page += 1) {
      const result = await api.ledgerPage(offset, LEDGER_PAGE_SIZE);
      for (const entry of result.entries) {
        if (entry.userId !== accountId || seen.has(entry.id)) continue;
        seen.add(entry.id);
        entries.push(entry);
      }
      if (!result.hasMore) break;
      if (result.entries.length === 0) {
        included = false;
        warning = '余额流水分页提前结束，备份未包含完整流水';
        break;
      }
      offset += LEDGER_PAGE_SIZE;
      if (page === LEDGER_MAX_PAGES - 1) {
        included = false;
        warning = '余额流水超过分页上限，备份只包含部分流水';
      }
    }
  } catch {
    included = false;
    warning = '余额流水获取失败，备份未包含完整流水';
  }

  return {
    capturedAt,
    included,
    entries,
    wallet,
    ...(warning === undefined ? {} : { warning }),
  };
}

async function assetifyMessages(
  messages: readonly ChatMessage[],
  collector: MediaCollector,
): Promise<ChatMessage[]> {
  return Promise.all(
    messages.map(async (message) => {
      const next: ChatMessage = { ...message };
      if (message.attachments !== undefined) {
        next.attachments = await Promise.all(
          message.attachments.map(async (attachment) => {
            if (attachment.dataUrl === undefined) return { ...attachment };
            if (!attachment.dataUrl.startsWith('data:')) return { ...attachment };
            const ref = await collector.addDataUrl(
              attachment.dataUrl,
              attachment.mime,
              `附件 ${attachment.name}`,
            );
            return ref === null ? { ...attachment } : { ...attachment, dataUrl: ref };
          }),
        );
      }
      if (message.images !== undefined) {
        next.images = await Promise.all(
          message.images.map(async (image, index) => {
            if (!image.startsWith('data:')) return image;
            const ref = await collector.addDataUrl(image, 'image/png', `消息图片 ${message.id} #${index + 1}`);
            return ref ?? image;
          }),
        );
      }
      if (message.audioUrl !== undefined) {
        if (message.audioUrl.startsWith('data:')) {
          const ref = await collector.addDataUrl(
            message.audioUrl,
            'audio/mpeg',
            `消息语音 ${message.id}`,
          );
          if (ref !== null) next.audioUrl = ref;
        } else if (message.audioUrl.startsWith('blob:')) {
          delete next.audioUrl;
          collector.warnings.push(`消息 ${message.id} 的临时语音 URL 无法持久化，已跳过`);
        }
      }
      return next;
    }),
  );
}

async function assetifyPresets(
  presets: readonly Preset[],
  collector: MediaCollector,
): Promise<Preset[]> {
  return Promise.all(
    presets.map(async (preset) => {
      if (!preset.avatar?.startsWith('data:')) return { ...preset };
      const ref = await collector.addDataUrl(preset.avatar, 'image/png', `角色卡头像 ${preset.name}`);
      return ref === null ? { ...preset } : { ...preset, avatar: ref };
    }),
  );
}

async function assetifyGallery(
  gallery: readonly GalleryImage[],
  collector: MediaCollector,
): Promise<GalleryImage[]> {
  return Promise.all(
    gallery.map(async (image) => {
      if (!image.dataUrl.startsWith('data:')) {
        collector.warnings.push(`图库图片 ${image.id} 不是 data URL，已原样保留`);
        return { ...image };
      }
      const ref = await collector.addDataUrl(image.dataUrl, image.mime, `图库图片 ${image.id}`);
      return ref === null ? { ...image } : { ...image, dataUrl: ref };
    }),
  );
}

async function assetifyMoments(
  moments: readonly Moment[],
  collector: MediaCollector,
): Promise<Moment[]> {
  return Promise.all(moments.map(async (moment) => ({
    ...moment,
    authorAvatar: moment.authorAvatar?.startsWith('data:')
      ? (await collector.addDataUrl(moment.authorAvatar, 'image/png', `朋友圈头像 ${moment.id}`) ?? moment.authorAvatar)
      : moment.authorAvatar,
    images: await Promise.all(moment.images.map(async (image, index) =>
      image.startsWith('data:')
        ? (await collector.addDataUrl(image, 'image/png', `朋友圈图片 ${moment.id} #${index + 1}`) ?? image)
        : image,
    )),
  })));
}

async function collectMimo(
  mimo: BackupMimoApi,
  accountId: string,
  messageIds: ReadonlySet<string>,
  collector: MediaCollector,
): Promise<BackupMimoFile> {
  const snapshot = await mimo.exportSnapshot(accountId);
  const audio: BackupMimoFile['audio'] = [];
  for (const item of snapshot.audio) {
    const ref = await collector.addBlob(item.blob, `TTS 音频 ${item.key}`);
    if (ref === null) continue;
    const sha256 = ref.slice('asset:'.length);
    const links = item.links.filter((link) => {
      if (messageIds.has(link)) return true;
      collector.warnings.push(`TTS 音频 ${item.key} 关联的消息 ${link} 不存在，已跳过该关联`);
      return false;
    });
    audio.push({
      key: item.key,
      id: item.id,
      sha256,
      bytes: item.blob.size,
      mime: item.mime,
      links,
      preview: item.preview,
      createdAt: item.createdAt,
      mediaRef: ref,
    });
  }

  let reference: BackupMimoFile['reference'];
  if (snapshot.reference !== undefined) {
    const ref = await collector.addBlob(snapshot.reference.blob, '参考音频');
    if (ref !== null) {
      reference = {
        name: snapshot.reference.name,
        sha256: ref.slice('asset:'.length),
        consent: true,
        mime: snapshot.reference.blob.type || 'application/octet-stream',
        mediaRef: ref,
      };
    }
  }

  return {
    audio,
    modes: [...snapshot.modes],
    ...(reference === undefined ? {} : { reference }),
  };
}

async function collectTtsPreferences(
  tts: BackupTtsApi,
  warnings: string[],
): Promise<BackupMimoFile['serverPreferences']> {
  try {
    const account = await tts.account();
    const parsed = preferencesSchema.safeParse(account.preferences);
    if (!parsed.success) {
      warnings.push('服务端 TTS 偏好格式不合法，已跳过');
      return undefined;
    }
    return parsed.data;
  } catch {
    warnings.push('服务端 TTS 偏好暂不可用，备份只包含本地语音数据');
    return undefined;
  }
}

/** 采集 + 打包 + 加密，返回可下载的 Blob 与摘要。 */
export async function collectEncryptedBackup(
  password: string,
  options: CreateBackupOptions = {},
): Promise<EncryptedBackup> {
  if (password.length < 8) {
    throw new BackupError('PASSWORD', '备份密码至少需要 8 位');
  }
  const api = options.api ?? defaultBackupApi;
  const tts = options.tts ?? defaultTtsApi;
  const mimo = options.mimo ?? mimoStorage;
  const now = options.now ?? Date.now();
  const randomBytes = options.randomBytes ?? secureRandomBytes;
  const account = await resolveAccount(options, api);

  const db = getDb();
  const [conversations, messages, presets, providers, memories, gallery, moments, automationEvents] = await Promise.all([
    db.conversations.toArray(),
    db.messages.toArray(),
    db.presets.toArray(),
    db.providers.toArray(),
    db.memories.toArray(),
    db.gallery.filter(image => image.userId === undefined || image.userId === account.id).toArray(),
    db.moments.filter(moment => moment.userId === undefined || moment.userId === account.id).toArray(),
    db.automationEvents.filter(event => event.userId === undefined || event.userId === account.id).toArray(),
  ]);

  const collector = new MediaCollector();
  const warnings: string[] = [];
  const [awareMessages, awarePresets, awareGallery, awareMoments] = await Promise.all([
    assetifyMessages(messages, collector),
    assetifyPresets(presets, collector),
    assetifyGallery(gallery, collector),
    assetifyMoments(moments, collector),
  ]);
  const messageIds = new Set(messages.map((message) => message.id));
  const mimoFile = await collectMimo(mimo, account.id, messageIds, collector);
  const serverPreferences = await collectTtsPreferences(tts, warnings);
  if (serverPreferences !== undefined) mimoFile.serverPreferences = serverPreferences;
  const localStorage = collectBackupLocalStorage(account.id);
  const ledger = await collectLedger(api, account.id, now);

  const collections: BackupCollections = {
    conversations: conversations.map((item) => ({ ...item })),
    messages: awareMessages,
    presets: awarePresets,
    providers: providers.map((item) => ({ ...item })),
    memories: memories.map((item) => ({ ...item })),
    gallery: awareGallery,
    moments: awareMoments,
    automationEvents,
    ledger: [],
  };
  warnings.push(...collector.warnings);
  if (ledger.warning !== undefined) warnings.push(ledger.warning);

  const mediaEntries = collector.entries();
  const counts: BackupCounts = {
    conversations: collections.conversations.length,
    messages: collections.messages.length,
    presets: collections.presets.length,
    providers: collections.providers.length,
    memories: collections.memories.length,
    gallery: collections.gallery.length,
    media: collector.size,
    localStorageKeys: Object.keys(localStorage).length,
    ledger: ledger.entries.length,
    ttsAudio: mimoFile.audio.length,
    ttsReferences: mimoFile.reference === undefined ? 0 : 1,
    moments: awareMoments.length,
    automationEvents: automationEvents.length,
  };
  const backupId = bytesToHex(randomBytes(16));
  const manifest: BackupManifest = {
    format: 'zxtalk.backup.manifest',
    version: 1,
    backupId,
    exportedAt: now,
    account,
    counts,
    media: { count: collector.size, bytes: collector.byteLength, entries: mediaEntries },
    ledger: {
      included: ledger.included,
      count: ledger.entries.length,
      capturedAt: ledger.capturedAt,
      ...(ledger.warning === undefined ? {} : { warning: ledger.warning }),
    },
    warnings,
  };
  const payload: ArchivePayload = {
    manifest,
    collections,
    localStorage,
    mimo: mimoFile,
    accountHistory: ledger,
    media: collector.bytes(),
  };
  const zip = packArchive(payload);
  const encrypted = await encryptContainer(zip, password, randomBytes);
  const blob = new Blob([encrypted], { type: 'application/octet-stream' });
  const summary = {
    backupId,
    exportedAt: now,
    accountId: account.id,
    accountEmail: account.email,
    counts,
    mediaBytes: collector.byteLength,
    ledger: manifest.ledger,
    warnings,
  };
  return {
    blob,
    filename: buildBackupFilename(new Date(now)),
    summary,
    manifest,
  };
}

export { collectLedger };
